// ============================================================================
// Record Collaboration consumer registry
// ============================================================================
//
// This is deliberately NOT a routeDataCatalog entry and it does not alter the
// 77-surface manifest. Comments and audit history are a shared record widget:
// the finite list below records only mounts that actually render it. A page
// retaining `legacy.all` is not made permanently owned merely because it has a
// comments box.
// ============================================================================

export const RECORD_COLLABORATION_RESOURCE = Object.freeze({
  COMMENTS: 'shared.record-collaboration.comments',
  MENTIONABLE_PROFILES: 'shared.record-collaboration.mentionable-profiles',
  ACTIVITY: 'shared.record-collaboration.activity',
});

// Signed URLs are a separate component-scoped concern from the comments RPC:
// one mounted comment may contain several private objects, each with its own
// bucket/path/cache lifetime. This is still deliberately NOT a route resource
// and does not extend the 77-surface manifest.
export const RECORD_COLLABORATION_ATTACHMENT_RESOURCE = Object.freeze({
  SIGNED_URL: 'shared.record-collaboration.comment-attachment.signed-url',
});

export const RECORD_COLLABORATION_MENTION_POLICY = Object.freeze({
  ALL_AUTHORIZED: 'all-authorized',
  TODO_PARTICIPANTS: 'todo-participants',
});

export type RecordCollaborationMentionPolicy =
  (typeof RECORD_COLLABORATION_MENTION_POLICY)[keyof typeof RECORD_COLLABORATION_MENTION_POLICY];

export type RecordCollaborationConsumer = {
  readonly source: string;
  readonly surface: string;
  readonly entityType: string;
  readonly entityIdContract: string;
  readonly comments: boolean;
  readonly mentionPolicy: RecordCollaborationMentionPolicy | null;
  readonly activityLimit: number;
};

export type RecordCollaborationCommentConsumer = RecordCollaborationConsumer & {
  readonly comments: true;
  readonly mentionPolicy: RecordCollaborationMentionPolicy;
};

const allAuthorized = RECORD_COLLABORATION_MENTION_POLICY.ALL_AUTHORIZED;

// One entry per RecordCollaborationSection JSX mount. `surface` is a receipt
// of where the mount appears, not a statement that the shared resource owns
// the whole surface. The independent static proof checks these exact source
// mounts, their mode/policy contracts, and their existence in SURFACE_MANIFEST.
export const RECORD_COLLABORATION_CONSUMERS: readonly RecordCollaborationConsumer[] = Object.freeze([
  Object.freeze({
    source: 'src/broiler/BroilerBatchPage.tsx',
    surface: 'broiler.batches.detail',
    entityType: 'broiler.batch',
    entityIdContract: 'batch.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/broiler/PoultryDailyPage.tsx',
    surface: 'broiler.dailys.detail',
    entityType: 'poultry.daily',
    entityIdContract: 'record.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/cattle/CattleAnimalPage.tsx',
    surface: 'cattle.herds.detail',
    entityType: 'cattle.animal',
    entityIdContract: 'cow.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/cattle/CattleBatchPage.tsx',
    surface: 'cattle.batches.detail',
    entityType: 'cattle.processing',
    entityIdContract: 'batch.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/cattle/CattleBreedingView.tsx',
    surface: 'view.cattlebreeding',
    entityType: 'cattle.breeding',
    entityIdContract: '"cattle-breeding"',
    comments: false,
    mentionPolicy: null,
    activityLimit: 100,
  }),
  Object.freeze({
    source: 'src/cattle/CattleDailyPage.tsx',
    surface: 'cattle.dailys.detail',
    entityType: 'cattle.daily',
    entityIdContract: 'record.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/cattle/CattleForecastView.tsx',
    surface: 'view.cattleforecast',
    entityType: 'cattle.forecast',
    entityIdContract: '"cattle-forecast"',
    comments: false,
    mentionPolicy: null,
    activityLimit: 200,
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentChecklistEntryPage.tsx',
    surface: 'equipment.checklist.entry',
    entityType: 'equipment.item',
    entityIdContract: 'eq.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentDetail.tsx',
    surface: 'equipment.fleet.detail',
    entityType: 'equipment.item',
    entityIdContract: 'eq.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentFuelingEntryPage.tsx',
    surface: 'equipment.fueling.entry',
    entityType: 'equipment.item',
    entityIdContract: 'eq.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/layer/EggDailyPage.tsx',
    surface: 'layer.eggDailys.detail',
    entityType: 'egg.daily',
    entityIdContract: 'record.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/layer/LayerBatchPage.tsx',
    surface: 'layer.batches.detail',
    entityType: 'layer.batch',
    entityIdContract: 'batch.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/layer/LayerDailyPage.tsx',
    surface: 'layer.dailys.detail',
    entityType: 'layer.daily',
    entityIdContract: 'record.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/layer/LayerHousingPage.tsx',
    surface: 'layer.housings.detail',
    entityType: 'layer.housing',
    entityIdContract: 'housing.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/layer/RetirementHomeHistory.tsx',
    surface: 'layer.batches.detail',
    entityType: 'layer.housing',
    entityIdContract: 'housing.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/livestock/WeighInSessionPage.tsx',
    surface: 'view.weighinsessions',
    entityType: 'weighin.session',
    entityIdContract: 'session.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/pig/PigBatchPage.tsx',
    surface: 'pig.batches.detail',
    entityType: 'pig.batch',
    entityIdContract: 'group.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/pig/PigDailyPage.tsx',
    surface: 'pig.dailys.detail',
    entityType: 'pig.daily',
    entityIdContract: 'record.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/pig/SowsView.tsx',
    surface: 'view.sows',
    entityType: 'pig.breeder',
    entityIdContract: 'recordPig.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/processing/ProcessingCutSheetPage.tsx',
    surface: 'view.processingCutSheet',
    entityType: 'processing.record',
    entityIdContract: 'record.id',
    comments: false,
    mentionPolicy: null,
    activityLimit: 200,
  }),
  Object.freeze({
    source: 'src/processing/cutSheetIntake/UsualScreen.tsx',
    surface: 'view.processingCutSheet',
    entityType: 'processing.cut_sheet_preferences',
    entityIdContract: 'layout.program',
    comments: false,
    mentionPolicy: null,
    activityLimit: 200,
  }),
  Object.freeze({
    source: 'src/processing/ProcessingDrawer.tsx',
    surface: 'view.processing',
    entityType: 'processing.record',
    entityIdContract: 'recordId',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  // Projects (mig 256): the job drawer shows the job's Activity only; jobs
  // have no comments (owner decisions D0.6, D13).
  Object.freeze({
    source: 'src/projects/ProjectsRoute.tsx',
    surface: 'view.projects',
    entityType: 'project.job',
    entityIdContract: 'job.id',
    comments: false,
    mentionPolicy: null,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/sheep/SheepAnimalPage.tsx',
    surface: 'sheep.flocks.detail',
    entityType: 'sheep.animal',
    entityIdContract: 'animal.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/sheep/SheepBatchPage.tsx',
    surface: 'sheep.batches.detail',
    entityType: 'sheep.processing',
    entityIdContract: 'batch.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/sheep/SheepDailyPage.tsx',
    surface: 'sheep.dailys.detail',
    entityType: 'sheep.daily',
    entityIdContract: 'record.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/tasks/TaskInstancePage.tsx',
    surface: 'tasks.instance.detail',
    entityType: 'task.instance',
    entityIdContract: 'record.id',
    comments: true,
    mentionPolicy: allAuthorized,
    activityLimit: 50,
  }),
  Object.freeze({
    source: 'src/tasks/TodoItemPage.tsx',
    surface: 'tasks.todo.detail',
    entityType: 'todo.item',
    entityIdContract: 'item.id',
    comments: true,
    mentionPolicy: RECORD_COLLABORATION_MENTION_POLICY.TODO_PARTICIPANTS,
    activityLimit: 50,
  }),
]);

function hasComments(consumer: RecordCollaborationConsumer): consumer is RecordCollaborationCommentConsumer {
  return consumer.comments;
}

// This is a projection, not a second hand-maintained list. A newly registered
// comments mount therefore becomes a signed-URL consumer automatically, and
// the attachment receipt below fails until its exact surface proof is updated.
export const RECORD_COLLABORATION_COMMENT_ATTACHMENT_CONSUMERS: readonly RecordCollaborationCommentConsumer[] =
  Object.freeze(RECORD_COLLABORATION_CONSUMERS.filter(hasComments));
